/**
 * TBGenSurv Module 2 — Local Data Store
 * Simulates NMRL laboratory data — no backend needed.
 * In production: connects to PostgreSQL via Flask API.
 */

import { classifyTB, shouldTriggerWGS } from '../fhir/utils';

// ── LAB USERS ─────────────────────────────────────────────────────────
export const LAB_USERS = {
  'lab.tech.nmrl': { id: 'u1', name: 'Lab Technician',       role: 'lab_technician',  password: 'Lab@NMRL2026' },
  'lab.senior':    { id: 'u2', name: 'Senior Lab Scientist',  role: 'lab_senior',      password: 'Senior@2026'  },
  'lab.manager':   { id: 'u3', name: 'Laboratory Manager',    role: 'lab_manager',     password: 'Manager@2026' },
};

// ── SAMPLES QUEUE (arriving from Module 1 clinics) ────────────────────
export let SAMPLE_QUEUE = [
  {
    id: 'smp-001', sample_code: 'TBS-HAR-20260315-0042',
    status: 'RECEIVED_NMRL', received_date: '2026-03-18T10:30:00Z',
    collection_date: '2026-03-15T09:32:00Z', transit_days: 3,
    sample_type: 'Sputum', appearance: 'Mucopurulent', volume_ml: 3.5,
    clinic: { name: 'Mbare Polyclinic', district: 'Harare', province: 'Harare' },
    patient: { age: 34, sex: 'M', hiv_status: 'Positive', deident_hash: 'a1b2c3d4e5' },
    lab_result: null,
    processing_started: null, processing_completed: null,
    assigned_to: null, priority: 'routine',
  },
  {
    id: 'smp-002', sample_code: 'TBS-BUL-20260312-0018',
    status: 'PROCESSING', received_date: '2026-03-16T08:45:00Z',
    collection_date: '2026-03-12T07:30:00Z', transit_days: 4,
    sample_type: 'Sputum', appearance: 'Bloody', volume_ml: 2.8,
    clinic: { name: 'Mpilo Central Hospital', district: 'Bulawayo', province: 'Bulawayo' },
    patient: { age: 52, sex: 'F', hiv_status: 'Negative', deident_hash: 'f6g7h8i9j0' },
    lab_result: null,
    processing_started: '2026-03-16T09:00:00Z', processing_completed: null,
    assigned_to: 'u1', priority: 'urgent',
  },
  {
    id: 'smp-003', sample_code: 'TBS-MAN-20260314-0007',
    status: 'RECEIVED_NMRL', received_date: '2026-03-19T11:15:00Z',
    collection_date: '2026-03-14T06:00:00Z', transit_days: 5,
    sample_type: 'Sputum', appearance: 'Mucopurulent', volume_ml: 4.0,
    clinic: { name: 'Mutare Provincial Hospital', district: 'Mutare', province: 'Manicaland' },
    patient: { age: 28, sex: 'F', hiv_status: 'Unknown', deident_hash: 'k1l2m3n4o5' },
    lab_result: null,
    processing_started: null, processing_completed: null,
    assigned_to: null, priority: 'routine',
  },
  {
    id: 'smp-004', sample_code: 'TBS-HAR-20260310-0039',
    status: 'COMPLETE', received_date: '2026-03-14T09:00:00Z',
    collection_date: '2026-03-10T08:15:00Z', transit_days: 4,
    sample_type: 'Sputum', appearance: 'Mucopurulent', volume_ml: 3.0,
    clinic: { name: 'Epworth Clinic', district: 'Harare', province: 'Harare' },
    patient: { age: 41, sex: 'M', hiv_status: 'Positive', deident_hash: 'p6q7r8s9t0' },
    lab_result: {
      smear_result: 'Positive', smear_grade: '2+',
      genexpert_result: 'MTB DETECTED', genexpert_rif_resist: 'DETECTED',
      culture_result: 'Positive', dst_isoniazid: 'Resistant',
      dst_rifampicin: 'Resistant', dst_pyrazinamide: 'Susceptible',
      dst_ethambutol: 'Susceptible', tb_classification: 'MDR-TB',
      wgs_requested: true, wgs_request_date: '2026-03-16T10:00:00Z',
      result_date: '2026-03-16T14:00:00Z', notes: 'MDR-TB confirmed. WGS requested for genomic surveillance.',
    },
    processing_started: '2026-03-14T10:00:00Z',
    processing_completed: '2026-03-16T14:00:00Z',
    assigned_to: 'u2', priority: 'urgent',
  },
  {
    id: 'smp-005', sample_code: 'TBS-MID-20260316-0003',
    status: 'RECEIVED_NMRL', received_date: '2026-03-20T07:30:00Z',
    collection_date: '2026-03-16T14:20:00Z', transit_days: 4,
    sample_type: 'BALF', appearance: 'Clear', volume_ml: 5.0,
    clinic: { name: 'Gweru Provincial Hospital', district: 'Gweru', province: 'Midlands' },
    patient: { age: 19, sex: 'M', hiv_status: 'Unknown', deident_hash: 'u1v2w3x4y5' },
    lab_result: null,
    processing_started: null, processing_completed: null,
    assigned_to: null, priority: 'routine',
  },
  {
    id: 'smp-006', sample_code: 'TBS-MVG-20260315-0011',
    status: 'REJECTED', received_date: '2026-03-20T13:00:00Z',
    collection_date: '2026-03-08T10:00:00Z', transit_days: 12,
    sample_type: 'Sputum', appearance: 'Watery', volume_ml: 1.2,
    clinic: { name: 'Masvingo Provincial Hospital', district: 'Masvingo', province: 'Masvingo' },
    patient: { age: 67, sex: 'F', hiv_status: 'Positive', deident_hash: 'z6a7b8c9d0' },
    lab_result: null,
    processing_started: null, processing_completed: null,
    rejection_reason: 'Sample received after 12 days — viability compromised. Volume insufficient (<2ml). Clinic notified.',
    assigned_to: null, priority: 'routine',
  },
];

// ── WGS REQUEST QUEUE ─────────────────────────────────────────────────
export let WGS_QUEUE = [
  {
    id: 'wgs-001', sample_id: 'smp-004', sample_code: 'TBS-HAR-20260310-0039',
    tb_classification: 'MDR-TB', requested_date: '2026-03-16T10:00:00Z',
    status: 'PENDING', priority: 'urgent',
    clinic: 'Epworth Clinic', province: 'Harare',
    reason: ['Rifampicin resistance detected (GeneXpert)', 'Isoniazid resistance detected'],
  },
];

// ── STATS ─────────────────────────────────────────────────────────────
export function getLabStats() {
  return {
    total_received:    SAMPLE_QUEUE.length,
    pending_receipt:   SAMPLE_QUEUE.filter(s => s.status === 'RECEIVED_NMRL').length,
    processing:        SAMPLE_QUEUE.filter(s => s.status === 'PROCESSING').length,
    completed_today:   SAMPLE_QUEUE.filter(s => s.status === 'COMPLETE').length,
    rejected:          SAMPLE_QUEUE.filter(s => s.status === 'REJECTED').length,
    wgs_pending:       WGS_QUEUE.filter(w => w.status === 'PENDING').length,
    mdr_detected:      SAMPLE_QUEUE.filter(s => s.lab_result?.tb_classification === 'MDR-TB').length,
    turnaround_avg:    3.2,
  };
}

// ── DATA ACTIONS ──────────────────────────────────────────────────────
export function receiveSample(sampleId, technicianId) {
  SAMPLE_QUEUE = SAMPLE_QUEUE.map(s => {
    if (s.id !== sampleId) return s;
    return { ...s, status: 'PROCESSING', processing_started: new Date().toISOString(), assigned_to: technicianId };
  });
}

export function saveLabResult(sampleId, resultData) {
  const classification = classifyTB(resultData);
  const wgsTrigger     = shouldTriggerWGS({ ...resultData, tb_classification: classification.classification });
  
  const finalResult = {
    ...resultData,
    tb_classification: classification.classification,
    wgs_requested:     wgsTrigger.triggered,
    wgs_trigger_reasons: wgsTrigger.reasons,
    result_date: new Date().toISOString(),
  };

  SAMPLE_QUEUE = SAMPLE_QUEUE.map(s => {
    if (s.id !== sampleId) return s;
    return { ...s, status: 'COMPLETE', lab_result: finalResult, processing_completed: new Date().toISOString() };
  });

  // Auto-add to WGS queue if triggered
  if (wgsTrigger.triggered) {
    const sample = SAMPLE_QUEUE.find(s => s.id === sampleId);
    WGS_QUEUE = [...WGS_QUEUE, {
      id:                `wgs-${Date.now()}`,
      sample_id:         sampleId,
      sample_code:       sample?.sample_code || '',
      tb_classification: classification.classification,
      requested_date:    new Date().toISOString(),
      status:            'PENDING',
      priority:          'urgent',
      clinic:            sample?.clinic?.name || '',
      province:          sample?.clinic?.province || '',
      reason:            wgsTrigger.reasons,
    }];
  }

  return { result: finalResult, classification, wgsTrigger };
}

export function rejectSample(sampleId, reason) {
  SAMPLE_QUEUE = SAMPLE_QUEUE.map(s => {
    if (s.id !== sampleId) return s;
    return { ...s, status: 'REJECTED', rejection_reason: reason };
  });
}
